/**
 * Gateway manager — CRUD, connection orchestration, and event emission.
 *
 * Provides both standalone CRUD functions (for simple storage operations)
 * and a `GatewayManager` class that manages the full lifecycle including
 * WebSocket connections, auth handshakes, and pairing flows.
 */

import type {
  GatewayConfig,
  GatewayConnectionState,
  GatewayStore,
  DeviceKeypair,
} from '../types/gateway';
import type { HelloOkPayload } from '../types/protocol';
import { readJSON, writeJSON, readString, writeString, remove } from '../utils/storage';
import { STORAGE_KEYS, getInitialSessionKey } from '../utils/constants';
import { WSClient } from '../ws/client';
import { performHandshake, PairingRequiredError, HandshakeError } from '../auth/handshake';
import { PairingLoop } from '../auth/pairing';
import { generateDeviceKeypair } from '../auth/device';

// ── Storage Keys ──

/** Per-gateway device keypair storage key */
function deviceStorageKey(gatewayId: string): string {
  return `voice-app-device-${gatewayId}`;
}

// ── Storage ──

/** Load all gateways from localStorage */
export function loadGateways(): GatewayConfig[] {
  return readJSON<GatewayConfig[]>(STORAGE_KEYS.GATEWAYS, []);
}

/** Save all gateways to localStorage */
export function saveGateways(gateways: GatewayConfig[]): void {
  writeJSON(STORAGE_KEYS.GATEWAYS, gateways);
}

/** Get the active gateway ID */
export function getActiveGatewayId(): string | null {
  return readString(STORAGE_KEYS.ACTIVE_GATEWAY) || null;
}

/** Set the active gateway ID */
export function setActiveGatewayId(id: string): void {
  writeString(STORAGE_KEYS.ACTIVE_GATEWAY, id);
}

/** Get the active gateway config (or the first one if none selected) */
export function getActiveGateway(): GatewayConfig | null {
  const gateways = loadGateways();
  const activeId = getActiveGatewayId();
  return gateways.find((g) => g.id === activeId) || gateways[0] || null;
}

// ── Storage: Device Keypairs ──

/** Load a device keypair from localStorage (stored separately for security) */
function loadDeviceKeypair(gatewayId: string): DeviceKeypair | null {
  return readJSON<DeviceKeypair | null>(deviceStorageKey(gatewayId), null);
}

/** Save a device keypair to localStorage */
function saveDeviceKeypair(gatewayId: string, keypair: DeviceKeypair): void {
  writeJSON(deviceStorageKey(gatewayId), keypair);
}

/** Remove a device keypair from localStorage */
function removeDeviceKeypair(gatewayId: string): void {
  remove(deviceStorageKey(gatewayId));
}

// ── CRUD ──

/** Add a new gateway and persist */
export function addGateway(config: GatewayConfig): void {
  const gateways = loadGateways();
  // Store device keypair separately if present
  if (config.device) {
    saveDeviceKeypair(config.id, config.device);
  }
  // Save config without private key in main store
  const sanitized = { ...config, device: config.device ? { id: config.device.id, publicKey: config.device.publicKey, privateKey: '' } : null };
  gateways.push(sanitized);
  saveGateways(gateways);
  if (!getActiveGatewayId()) {
    setActiveGatewayId(config.id);
  }
}

/** Update an existing gateway by ID */
export function updateGateway(
  id: string,
  updates: Partial<GatewayConfig>,
): void {
  const gateways = loadGateways();
  const idx = gateways.findIndex((g) => g.id === id);
  if (idx >= 0) {
    // If device keypair is being updated, save it separately
    if (updates.device) {
      saveDeviceKeypair(id, updates.device);
      updates = { ...updates, device: { id: updates.device.id, publicKey: updates.device.publicKey, privateKey: '' } };
    }
    Object.assign(gateways[idx], updates);
    saveGateways(gateways);
  }
}

/** Remove a gateway by ID */
export function removeGateway(id: string): void {
  let gateways = loadGateways();
  gateways = gateways.filter((g) => g.id !== id);
  saveGateways(gateways);
  removeDeviceKeypair(id);

  if (getActiveGatewayId() === id) {
    setActiveGatewayId(gateways[0]?.id || '');
  }
}

// ── Factory ──

/** Create a new gateway config object (not yet persisted) */
export function createGatewayConfig(params: {
  url: string;
  gatewayToken: string;
  name?: string;
}): GatewayConfig {
  // Parse hostname for default name
  let defaultName = 'Gateway';
  try {
    const urlForParsing = params.url.startsWith('ws')
      ? params.url.replace(/^ws/, 'http')
      : params.url;
    defaultName = new URL(urlForParsing).hostname;
  } catch { /* use default */ }

  return {
    id: crypto.randomUUID(),
    name: params.name || defaultName,
    url: params.url,
    sessionKey: getInitialSessionKey(),
    device: null,
    deviceToken: null,
    gatewayToken: params.gatewayToken,
    status: 'new',
    lastConnected: null,
  };
}

/** Get the full gateway store state */
export function getGatewayStore(): GatewayStore {
  return {
    gateways: loadGateways(),
    activeGatewayId: getActiveGatewayId(),
  };
}

// ── Event Types ──

/** Callback for gateway state changes */
export type StateChangeCallback = (gatewayId: string, newState: GatewayConnectionState) => void;

/** Callback for gateway connected events */
export type ConnectedCallback = (gatewayId: string) => void;

/** Callback for gateway disconnected events */
export type DisconnectedCallback = (gatewayId: string, reason: string) => void;

/** Callback for pairing required events */
export type PairingRequiredCallback = (gatewayId: string, deviceId: string) => void;

// ── GatewayManager Class ──

/**
 * Manages the full lifecycle of multiple gateway connections.
 *
 * Handles:
 * - Gateway CRUD with localStorage persistence
 * - WebSocket connection management per gateway
 * - Auth handshake orchestration
 * - Pairing flow management
 * - Event emission for React state updates
 *
 * @example
 * ```ts
 * const manager = new GatewayManager();
 * manager.onStateChange((id, state) => console.log(id, state));
 * const config = await manager.addGateway('wss://my-gw.ts.net', 'token123');
 * await manager.connectGateway(config.id);
 * ```
 */
export class GatewayManager {
  /** Active WSClient instances keyed by gateway ID */
  private clients = new Map<string, WSClient>();

  /** Active PairingLoop instances keyed by gateway ID */
  private pairingLoops = new Map<string, PairingLoop>();

  /** Runtime connection states keyed by gateway ID */
  private connectionStates = new Map<string, GatewayConnectionState>();

  // ── Reconnect ──

  /** Pending reconnect timers keyed by gateway ID */
  private reconnectTimers = new Map<string, ReturnType<typeof setTimeout>>();

  /**
   * Gateways for which auto-reconnect is suppressed.
   *
   * Populated when:
   * - `disconnectGateway` is called (user-initiated disconnect)
   * - A `HandshakeError` with a protocol/auth code is thrown
   *
   * Cleared when `connectGateway` starts a fresh attempt.
   */
  private reconnectSuppressed = new Set<string>();

  /** Delay between automatic reconnect attempts (ms). */
  private static readonly RECONNECT_DELAY_MS = 1000;

  // ── Event Listeners ──
  private stateChangeListeners = new Set<StateChangeCallback>();
  private connectedListeners = new Set<ConnectedCallback>();
  private disconnectedListeners = new Set<DisconnectedCallback>();
  private pairingRequiredListeners = new Set<PairingRequiredCallback>();

  // ── CRUD Operations ──

  /**
   * Add a new gateway configuration.
   *
   * Generates an Ed25519 keypair, creates the config, and persists to storage.
   *
   * @param url - WebSocket URL (wss://...)
   * @param token - Gateway authentication token
   * @param name - Optional display name
   * @returns The created GatewayConfig
   */
  async addGateway(url: string, token: string, name?: string): Promise<GatewayConfig> {
    // Generate Ed25519 keypair for this gateway
    const keypair = await generateDeviceKeypair();

    // Create config
    const config = createGatewayConfig({ url, gatewayToken: token, name });
    config.device = keypair;

    // Persist
    addGateway(config);

    return config;
  }

  /**
   * Remove a gateway and clean up all associated resources.
   *
   * Disconnects the WebSocket, stops any pairing loop, and removes from storage.
   *
   * @param id - Gateway ID to remove
   */
  removeGateway(id: string): void {
    // Disconnect if active
    this.disconnectGateway(id);

    // Stop any pairing loop
    const loop = this.pairingLoops.get(id);
    if (loop) {
      loop.cancel();
      this.pairingLoops.delete(id);
    }

    // Clean up connection state
    this.connectionStates.delete(id);

    // Remove from storage
    removeGateway(id);
  }

  /**
   * Partially update a gateway configuration.
   *
   * @param id - Gateway ID to update
   * @param updates - Partial config updates (name, sessionKey, etc.)
   */
  updateGateway(id: string, updates: Partial<GatewayConfig>): void {
    updateGateway(id, updates);
  }

  /** Get all saved gateway configurations */
  getGateways(): GatewayConfig[] {
    return loadGateways();
  }

  /** Get the currently active gateway configuration (or null) */
  getActiveGateway(): GatewayConfig | null {
    return getActiveGateway();
  }

  /**
   * Switch the focused gateway.
   *
   * Changes which gateway receives input (text/voice). Does NOT
   * disconnect other gateways — all remain connected simultaneously.
   *
   * If the target gateway isn't connected yet, initiates connection.
   *
   * @param id - Gateway ID to focus
   */
  async setActiveGateway(id: string): Promise<void> {
    // Set new focus
    setActiveGatewayId(id);

    // Connect the target if not already connected
    const client = this.clients.get(id);
    if (!client) {
      await this.connectGateway(id);
    }
  }

  /**
   * Connect all saved gateways simultaneously.
   *
   * Iterates through all persisted gateway configs and initiates
   * connections for any that aren't already connected. Used on app
   * startup to establish all gateway connections at once.
   */
  async connectAllGateways(): Promise<void> {
    const gateways = loadGateways();
    const promises: Promise<void>[] = [];

    for (const gw of gateways) {
      // Skip if already connected
      const existing = this.clients.get(gw.id);
      if (existing) continue;

      // Only connect if the gateway has credentials
      if (gw.deviceToken || gw.gatewayToken) {
        promises.push(
          this.connectGateway(gw.id).catch((err) => {
            console.warn(`[GatewayManager] Failed to connect gateway ${gw.name}:`, err);
          }),
        );
      }
    }

    await Promise.allSettled(promises);
  }

  /**
   * Get all currently active WSClient instances.
   *
   * @returns Map of gateway ID → WSClient for all connected gateways
   */
  getAllClients(): Map<string, WSClient> {
    return new Map(this.clients);
  }

  // ── Connection Management ──

  /**
   * Initiate a WebSocket connection and auth handshake for a gateway.
   *
   * Handles the full flow:
   * 1. Creates WSClient and connects
   * 2. Performs auth handshake
   * 3. On success: stores device token, updates status to "paired"/"connected"
   * 4. On NOT_PAIRED: updates status to "pending", starts pairing loop
   * 5. On error: updates status to "error"
   *
   * @param id - Gateway ID to connect
   */
  async connectGateway(id: string): Promise<void> {
    // Load full config with keypair
    const gateways = loadGateways();
    const config = gateways.find((g) => g.id === id);
    if (!config) {
      throw new Error(`Gateway not found: ${id}`);
    }

    // Restore device keypair from separate storage
    const keypair = loadDeviceKeypair(id);
    if (keypair) {
      config.device = keypair;
    }

    // Disconnect existing connection if any (also clears timers + sets suppression)
    this.disconnectGateway(id);

    // Fresh connection attempt — allow auto-reconnect from this point forward
    this.reconnectSuppressed.delete(id);

    // Update state to connecting
    this.setConnectionState(id, {
      status: 'connecting',
      tickIntervalMs: null,
      ttsConfig: null,
      gatewayVersion: null,
      caps: [],
    });

    // Create WSClient
    const client = new WSClient({
      url: config.url,
      autoReconnect: false, // We manage reconnection ourselves
      shouldReconnect: (code) => {
        // Don't auto-reconnect on auth failures
        if (code === 1008 || code === 4001) return false;
        return true;
      },
    });

    this.clients.set(id, client);

    const isCurrentClient = () => this.clients.get(id) === client;

    // Listen for close events
    client.on('close', (code, reason) => {
      if (!isCurrentClient()) return;

      if (code === 1008 && reason?.toLowerCase().includes('origin')) {
        this.setConnectionState(id, {
          status: 'origin_blocked',
          tickIntervalMs: null,
          ttsConfig: null,
          gatewayVersion: null,
          caps: [],
        });
        // Don't emit disconnected — the store's onDisconnected handler
        // would overwrite origin_blocked back to 'disconnected'.
        return;
      }
      this.setConnectionState(id, {
        status: 'disconnected',
        tickIntervalMs: null,
        ttsConfig: null,
        gatewayVersion: null,
        caps: [],
      });
      this.emitDisconnected(id, reason || 'Connection closed');

      // Schedule automatic reconnect unless suppressed (user-initiated
      // disconnect or unrecoverable auth failure) or a pairing loop is
      // already managing reconnection.
      if (!this.reconnectSuppressed.has(id) && !this.pairingLoops.has(id)) {
        this.scheduleReconnect(id);
      }
    });

    // Connect WebSocket
    client.connect();

    try {
      // Perform auth handshake
      const { helloOk, gateway: updatedGw } = await performHandshake(client, config);
      if (!isCurrentClient()) return;

      // Success! Update config with device info
      this.handleHandshakeSuccess(id, updatedGw, helloOk, client);
    } catch (err) {
      if (!isCurrentClient()) return;

      if (err instanceof PairingRequiredError) {
        // Device needs approval
        this.handlePairingRequired(id, config, err.deviceId);
      } else if (err instanceof HandshakeError) {
        if (err.code) {
          // Auth/protocol error — do not auto-reconnect
          this.reconnectSuppressed.add(id);
          this.setConnectionState(id, {
            status: 'error',
            tickIntervalMs: null,
            ttsConfig: null,
            gatewayVersion: null,
            caps: [],
          });
          updateGateway(id, { status: 'error' });
          console.error(`[GatewayManager] Handshake error for ${id}:`, err.message);
        } else {
          // Recoverable handshake failures (for example, the socket closed
          // before the challenge arrived) should not suppress the reconnect
          // loop. The close handler owns the retry scheduling for that path.
          console.warn(`[GatewayManager] Recoverable handshake error for ${id}:`, err.message);
        }
      } else {
        // Unknown error (e.g. network failure before handshake) — the close
        // event will fire and schedule a reconnect via the close handler above.
        console.error(`[GatewayManager] Connection error for ${id}:`, err);
      }
    }
  }

  /**
   * Disconnect a specific gateway's WebSocket connection.
   *
   * @param id - Gateway ID to disconnect
   */
  disconnectGateway(id: string): void {
    // Suppress auto-reconnect for this gateway (user-initiated disconnect)
    this.reconnectSuppressed.add(id);
    this.clearReconnectTimer(id);

    const client = this.clients.get(id);
    if (client) {
      client.disconnect();
      this.clients.delete(id);
    }

    // Stop any pairing loop
    const loop = this.pairingLoops.get(id);
    if (loop) {
      loop.cancel();
      this.pairingLoops.delete(id);
    }
  }

  /**
   * Get the current connection state for a gateway.
   *
   * @param id - Gateway ID
   * @returns Connection state, or a default "disconnected" state
   */
  getConnectionState(id: string): GatewayConnectionState {
    return this.connectionStates.get(id) ?? {
      status: 'disconnected',
      tickIntervalMs: null,
      ttsConfig: null,
      gatewayVersion: null,
      caps: [],
    };
  }

  /**
   * Get the gateway version reported by the server in hello-ok.
   *
   * @param id - Gateway ID
   * @returns Version string or null if not connected / not reported
   */
  getGatewayVersion(id: string): string | null {
    return this.connectionStates.get(id)?.gatewayVersion ?? null;
  }

  /**
   * Get the WSClient instance for a gateway (for chat/voice modules).
   *
   * @param id - Gateway ID
   * @returns WSClient or null if not connected
   */
  getClient(id: string): WSClient | null {
    return this.clients.get(id) ?? null;
  }

  /**
   * Get the active gateway's WSClient instance.
   *
   * @returns WSClient or null if no active gateway or not connected
   */
  getActiveClient(): WSClient | null {
    const activeId = getActiveGatewayId();
    if (!activeId) return null;
    return this.getClient(activeId);
  }

  // ── Event Registration ──

  /**
   * Register a callback for gateway state changes.
   *
   * @param callback - Called with (gatewayId, newState) on every state change
   * @returns Unsubscribe function
   */
  onStateChange(callback: StateChangeCallback): () => void {
    this.stateChangeListeners.add(callback);
    return () => { this.stateChangeListeners.delete(callback); };
  }

  /**
   * Register a callback for gateway connected events.
   *
   * @param callback - Called with (gatewayId) when a gateway becomes connected
   * @returns Unsubscribe function
   */
  onConnected(callback: ConnectedCallback): () => void {
    this.connectedListeners.add(callback);
    return () => { this.connectedListeners.delete(callback); };
  }

  /**
   * Register a callback for gateway disconnected events.
   *
   * @param callback - Called with (gatewayId, reason) when disconnected
   * @returns Unsubscribe function
   */
  onDisconnected(callback: DisconnectedCallback): () => void {
    this.disconnectedListeners.add(callback);
    return () => { this.disconnectedListeners.delete(callback); };
  }

  /**
   * Register a callback for pairing required events.
   *
   * @param callback - Called with (gatewayId, deviceId) when NOT_PAIRED received
   * @returns Unsubscribe function
   */
  onPairingRequired(callback: PairingRequiredCallback): () => void {
    this.pairingRequiredListeners.add(callback);
    return () => { this.pairingRequiredListeners.delete(callback); };
  }

  /**
   * Clean up all connections and listeners.
   *
   * Call this when the manager is no longer needed.
   */
  destroy(): void {
    // Cancel all pending reconnect timers first (before disconnectGateway
    // adds more to reconnectSuppressed and fires the close handlers).
    for (const [id] of this.reconnectTimers) {
      this.clearReconnectTimer(id);
    }

    // Disconnect all gateways
    for (const [id] of this.clients) {
      this.disconnectGateway(id);
    }

    // Cancel all pairing loops
    for (const [, loop] of this.pairingLoops) {
      loop.cancel();
    }
    this.pairingLoops.clear();

    // Clear listeners
    this.stateChangeListeners.clear();
    this.connectedListeners.clear();
    this.disconnectedListeners.clear();
    this.pairingRequiredListeners.clear();
  }

  // ── Private Helpers ──

  /**
   * Handle a successful handshake — update config, start keepalive, emit events.
   */
  private handleHandshakeSuccess(
    id: string,
    updatedGw: GatewayConfig,
    helloOk: HelloOkPayload,
    client: WSClient,
  ): void {
    const tickIntervalMs = helloOk.policy.tickIntervalMs;

    // Update stored config
    const updates: Partial<GatewayConfig> = {
      device: updatedGw.device,
      status: 'paired',
      lastConnected: new Date().toISOString(),
    };

    if (helloOk.auth?.deviceToken) {
      updates.deviceToken = helloOk.auth.deviceToken;
      updates.gatewayToken = null; // Clear gateway token after successful pairing
    }

    updateGateway(id, updates);

    // Mark client as authenticated and start keepalive
    client.setAuthenticated();
    client.startKeepalive(tickIntervalMs);

    // Update connection state
    this.setConnectionState(id, {
      status: 'connected',
      tickIntervalMs,
      ttsConfig: null,
      gatewayVersion: helloOk.server?.version ?? null,
      caps: helloOk.caps ?? [],
    });

    // Emit connected event
    this.emitConnected(id);
  }

  /**
   * Handle NOT_PAIRED — update status, start pairing loop, emit event.
   */
  private handlePairingRequired(
    id: string,
    config: GatewayConfig,
    deviceId: string,
  ): void {
    // Update status
    this.setConnectionState(id, {
      status: 'pending',
      tickIntervalMs: null,
      ttsConfig: null,
      gatewayVersion: null,
      caps: [],
    });
    updateGateway(id, { status: 'pending', device: config.device });

    // Emit pairing required event
    this.emitPairingRequired(id, deviceId);

    // Start pairing loop — retry connection periodically
    const loop = new PairingLoop(deviceId, id, async () => {
      return await this.attemptReconnect(id);
    });

    loop.onStateChange = (state) => {
      if (state.status === 'approved') {
        // Pairing was approved — gateway is now connected
        // (handled by attemptReconnect success)
      } else if (state.status === 'expired') {
        this.setConnectionState(id, {
          status: 'error',
          tickIntervalMs: null,
          ttsConfig: null,
          gatewayVersion: null,
      caps: [],
        });
      }
    };

    this.pairingLoops.set(id, loop);
    loop.start();
  }

  /**
   * Attempt to reconnect a gateway (used by pairing loop).
   *
   * @returns true if the gateway is now connected (approved)
   */
  private async attemptReconnect(id: string): Promise<boolean> {
    const gateways = loadGateways();
    const config = gateways.find((g) => g.id === id);
    if (!config) return false;

    // Restore device keypair
    const keypair = loadDeviceKeypair(id);
    if (keypair) {
      config.device = keypair;
    }

    // Disconnect existing client
    const oldClient = this.clients.get(id);
    if (oldClient) {
      oldClient.disconnect();
      this.clients.delete(id);
    }

    // Create new client
    const client = new WSClient({
      url: config.url,
      autoReconnect: false,
    });
    this.clients.set(id, client);

    client.on('close', (code, reason) => {
      if (code === 1008 && reason?.toLowerCase().includes('origin')) {
        this.setConnectionState(id, {
          status: 'origin_blocked',
          tickIntervalMs: null,
          ttsConfig: null,
          gatewayVersion: null,
      caps: [],
        });
        return;
      }
      this.setConnectionState(id, {
        status: 'disconnected',
        tickIntervalMs: null,
        ttsConfig: null,
        gatewayVersion: null,
      caps: [],
      });
      this.emitDisconnected(id, reason || 'Connection closed');
    });

    client.connect();

    try {
      const { helloOk, gateway: updatedGw } = await performHandshake(client, config);
      this.handleHandshakeSuccess(id, updatedGw, helloOk, client);

      // Stop the pairing loop
      const loop = this.pairingLoops.get(id);
      if (loop) {
        loop.cancel();
        this.pairingLoops.delete(id);
      }

      return true;
    } catch (err) {
      if (err instanceof PairingRequiredError) {
        // Still not approved — continue waiting
        client.disconnect();
        this.clients.delete(id);
        return false;
      }
      // Other error — disconnect and return false
      client.disconnect();
      this.clients.delete(id);
      return false;
    }
  }

  /**
   * Update the connection state for a gateway and emit the state change event.
   */
  private setConnectionState(id: string, state: GatewayConnectionState): void {
    this.connectionStates.set(id, state);
    this.emitStateChange(id, state);
  }

  // ── Reconnect Helpers ──

  /**
   * Schedule a reconnect attempt for a gateway after RECONNECT_DELAY_MS.
   *
   * Idempotent — clears any existing timer before scheduling a new one.
   */
  private scheduleReconnect(id: string): void {
    this.clearReconnectTimer(id);
    const timer = setTimeout(async () => {
      this.reconnectTimers.delete(id);
      // Guard: might have been suppressed between scheduling and firing
      if (this.reconnectSuppressed.has(id)) return;
      try {
        await this.connectGateway(id);
      } catch {
        // connectGateway handles its own error state; swallow here
      }
    }, GatewayManager.RECONNECT_DELAY_MS);
    this.reconnectTimers.set(id, timer);
  }

  /** Clear a pending reconnect timer for a gateway (if any). */
  private clearReconnectTimer(id: string): void {
    const timer = this.reconnectTimers.get(id);
    if (timer !== undefined) {
      clearTimeout(timer);
      this.reconnectTimers.delete(id);
    }
  }

  // ── Event Emission ──

  private emitStateChange(id: string, state: GatewayConnectionState): void {
    for (const cb of this.stateChangeListeners) {
      try { cb(id, state); } catch (e) { console.error('[GatewayManager] stateChange listener error:', e); }
    }
  }

  private emitConnected(id: string): void {
    for (const cb of this.connectedListeners) {
      try { cb(id); } catch (e) { console.error('[GatewayManager] connected listener error:', e); }
    }
  }

  private emitDisconnected(id: string, reason: string): void {
    for (const cb of this.disconnectedListeners) {
      try { cb(id, reason); } catch (e) { console.error('[GatewayManager] disconnected listener error:', e); }
    }
  }

  private emitPairingRequired(id: string, deviceId: string): void {
    for (const cb of this.pairingRequiredListeners) {
      try { cb(id, deviceId); } catch (e) { console.error('[GatewayManager] pairingRequired listener error:', e); }
    }
  }
}
